import React, { ReactNode, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'mui-modal-provider';

import { Preloader } from '../../../../common/preloader';
import { TechIssues } from '../../../../common/tech-issues';
import { Page } from '../../page/page';
import { Box } from '../../../../common/box';
import { ContentWrapper } from '../../../../common/content-wrapper';
import { InternalLinks, SECONDARY_BACKGROUND } from '../../../../../constants';
import { useStyles } from './kids-profile.styles';
import { links } from './kids-profile.items';
import { ProfileLink } from '../profile-link';
import { GET_DEPENDENTS } from '../../../../../graphql/profile/queries/get-dependents';
import { DependentProfile } from '../../../../../graphql/profile/models/kids.models';
import { getColor } from '../../../../../utils/kids/get-color';
import { AddProfile } from '../add-profile';
import { calculateAgeStatus } from '../../../../../utils/calculate-age-status';
import { v2025PP16783 } from '../../../../../constants/veeva';
import { isDependentCreated } from '../../../../../graphql/user/user.cache';
import { SuccessModal } from '../create-kids-profile/success-modal';

export const KidsProfile = (): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const { data, loading, error } = useQuery<{ getDependents: DependentProfile[] }>(GET_DEPENDENTS, { fetchPolicy: 'network-only' });
  const navigate = useNavigate();
  const isSuccess = useReactiveVar(isDependentCreated);
  const { showModal } = useModal();

  useEffect(() => {
    if (isSuccess) {
      showModal(SuccessModal, {});
      isDependentCreated(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (data?.getDependents && !data?.getDependents?.length) {
      navigate(InternalLinks.home);
    }
  }, [data]);

  return (
    <>
      {loading && <Preloader />}
      {!!error && <TechIssues />}
      {data && (
        <Page
          title={intl.formatMessage({ id: 'kidsPage.pageTitle' })}
          description={intl.formatMessage({ id: 'profilePage.description' })}
          style={`body {background-color: ${SECONDARY_BACKGROUND};}`}
        >
          <ContentWrapper withoutPaddings>
            <Box
              title={intl.formatMessage({ id: 'kidsPage.title' }, { sup: (chunks: ReactNode) => <sup>{chunks}</sup> })}
              isBackButton
            >
              <div className={classes.veevaNumber}>{v2025PP16783}</div>
              <h2 className={classes.subtitle}><FormattedMessage id="kidsPage.subtitle-1" /></h2>
              <ul className={classes.list}>
                {data?.getDependents?.map(({ firstName, lastName, dateOfBirth, id, active }, i) => {
                  const { isDeactivatedSoon, dateOfDeactivation } = calculateAgeStatus(
                    dateOfBirth,
                    intl,
                  );
                  return (
                    <li key={`${firstName}-${lastName}`}>
                      <ProfileLink
                        name={`${firstName} ${lastName}`}
                        url={`${InternalLinks.viewProfile}?id=${id}`}
                        letter={firstName[0]}
                        date={dateOfBirth}
                        color={getColor(i + 1)}
                        isDeactivatedSoon={isDeactivatedSoon}
                        dateOfDeactivation={dateOfDeactivation}
                        active={active}
                      />
                    </li>
                  );
                })}
              </ul>
              <h2 className={classes.subtitle}><FormattedMessage id="kidsPage.subtitle-2" /></h2>
              <ul className={classes.list}>
                {links.map(({ img, text, url }) => (
                  <li key={text}>
                    <ProfileLink image={img} text={text} url={url} />
                  </li>
                ))}
              </ul>
              <div className={classes.button}>
                <AddProfile />
              </div>
            </Box>
          </ContentWrapper>
        </Page>
      )}
    </>
  );
};
